import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  TextField,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import { checkCompliance, createDocument, uploadDocumentFile } from '../services/documentService';
import { useNavigate } from 'react-router-dom';

interface MissingDoc {
  id: string;
  name: string;
}

// First-access wizard that nudges (but does not force) a newly registered user
// to upload the mandatory documents required for their role.
const DocumentWizardDialog: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, currentTenant } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState<MissingDoc[]>([]);
  const [uploadedIds, setUploadedIds] = useState<Set<string>>(new Set());
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expireDates, setExpireDates] = useState<Record<string, string>>({});

  // Per-tenant key so a multi-tenant user is still nudged in each tenant.
  const seenKey = user && currentTenant ? `docWizardSeen_${user.id}_${currentTenant.id}` : null;

  useEffect(() => {
    if (!isAuthenticated || !user || !currentTenant) return;
    if (!seenKey || localStorage.getItem(seenKey)) return;
    // Only nudge regular users, not operators/admins.
    const role = currentTenant.role;
    if (role === 'admin' || role === 'operator') {
      localStorage.setItem(seenKey, '1');
      return;
    }

    let cancelled = false;
    setLoading(true);
    checkCompliance()
      .then((missingTypes) => {
        if (cancelled) return;
        if (Array.isArray(missingTypes) && missingTypes.length > 0) {
          setMissing(missingTypes.map((m: any) => ({ id: m.id, name: m.name })));
          setOpen(true);
        } else {
          // Nothing required: don't bother the user again.
          localStorage.setItem(seenKey, '1');
        }
      })
      .catch(() => {
        // On error, fail silently and don't block the app.
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, currentTenant, seenKey]);

  const dismiss = () => {
    if (seenKey) localStorage.setItem(seenKey, '1');
    setOpen(false);
  };

  const handleUpload = async (docTypeId: string, file: File) => {
    if (!user) return;
    setUploadingId(docTypeId);
    setError(null);
    try {
      const created = await createDocument({
        userId: user.id,
        documentTypeId: docTypeId,
        expireDate: expireDates[docTypeId] || null,
        link: '',
      } as any);
      await uploadDocumentFile(created.id, file);
      setUploadedIds((prev) => new Set(prev).add(docTypeId));
    } catch (err) {
      setError(t('doc_wizard_upload_error', 'Upload failed. You can try again later from "My Documents".'));
    } finally {
      setUploadingId(null);
    }
  };

  // La prima delle due condizioni non serviva: senza `open` non si disegna
  // nulla comunque, anche mentre il controllo e' in corso.
  if (!open) return null;

  const allDone = missing.length > 0 && missing.every((m) => uploadedIds.has(m.id));

  return (
    <Dialog open={open} onClose={dismiss} maxWidth="sm" fullWidth>
      <DialogTitle>{t('doc_wizard_title', 'Complete your profile')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'doc_wizard_subtitle',
            'Based on your role, the following documents are required. You can upload them now or later from "My Documents".'
          )}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <List>
          {missing.map((doc) => {
            const done = uploadedIds.has(doc.id);
            const isUploading = uploadingId === doc.id;
            return (
              <ListItem
                key={doc.id}
                secondaryAction={
                  done ? (
                    <Chip icon={<CheckCircleIcon />} label={t('doc_wizard_uploaded', 'Uploaded')} color="success" size="small" />
                  ) : (
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      startIcon={isUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                      disabled={isUploading}
                    >
                      {t('doc_wizard_upload', 'Upload')}
                      <input
                        hidden
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleUpload(doc.id, e.target.files[0]);
                          }
                        }}
                      />
                    </Button>
                  )
                }
              >
                <ListItemText
                  primary={doc.name}
                  secondary={
                    !done ? (
                      <TextField
                        type="date"
                        size="small"
                        variant="standard"
                        label={t('doc_wizard_expiry', 'Scadenza (opzionale)')}
                        value={expireDates[doc.id] || ''}
                        onChange={(e) => setExpireDates(prev => ({ ...prev, [doc.id]: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mt: 0.5, maxWidth: 200 }}
                      />
                    ) : undefined
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            );
          })}
        </List>

        {allDone && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {t('doc_wizard_all_done', 'All required documents have been uploaded. They will be reviewed by an operator.')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={dismiss} color="inherit">
          {t('doc_wizard_skip', 'Skip for now')}
        </Button>
        <Button
          onClick={() => {
            dismiss();
            navigate('/my-documents');
          }}
          variant="contained"
        >
          {t('doc_wizard_go_to_documents', 'Go to my documents')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentWizardDialog;
